// =============================================================================
// Payments (§7). Every payment taken at the branch, in one list.
//
// Until now a payment could only be seen from the member it belonged to, which
// answers "has this child paid?" and nothing else. The question the desk
// actually has at the end of a day is the other one: what came in, how, and
// against which receipt numbers — the list that gets reconciled against the
// paper book.
//
// So this screen leads with the totals and defaults to the current month.
// Branch scoping is in the view (0010), not here.
// =============================================================================

import { el, mount, formatDate } from '../lib/dom.js';
import { T } from '../lib/strings.js';
import { money } from '../config/school.js';
import { listPayments } from '../lib/api.js';
import { spinner } from '../lib/loading.js';

// Today in the school's timezone, not the browser's (§2.5).
function todayLocal() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Yangon', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}

export function paymentsScreen(root) {
  const today = todayLocal();
  let filters = {
    from: `${today.slice(0, 7)}-01`,   // the first of this month
    to: today,
    kind: '',
    method: '',
    search: '',
  };
  let rows = [];
  let loading = true;

  const view = el('div', { class: 'screen' });
  mount(root, view);

  async function load() {
    loading = true; render();
    try {
      rows = await listPayments(filters);
    } catch (err) {
      rows = [];
      mount(view, el('p', { class: 'error' }, err.message));
      loading = false; return;
    }
    loading = false; render();
  }

  function render() {
    const from = el('input', { type: 'date', class: 'text-input', value: filters.from, 'aria-label': T.from });
    const to = el('input', { type: 'date', class: 'text-input', value: filters.to, 'aria-label': T.to });
    const kind = el('select', { class: 'text-input', 'aria-label': T.colFor },
      el('option', { value: '' }, T.allKinds),
      ...Object.entries(T.kindShort).map(([v, label]) =>
        el('option', { value: v, selected: filters.kind === v }, label)));
    const method = el('select', { class: 'text-input', 'aria-label': T.colMethod },
      el('option', { value: '' }, T.allMethods),
      ...Object.entries(T.methods).map(([v, label]) =>
        el('option', { value: v, selected: filters.method === v }, label)));
    const search = el('input', {
      type: 'search', class: 'text-input', value: filters.search,
      placeholder: T.searchPayments, autocomplete: 'off', enterkeyhint: 'search',
      'aria-label': T.searchPayments,
    });

    function apply(e) {
      e?.preventDefault();
      filters = {
        from: from.value || null, to: to.value || null,
        kind: kind.value, method: method.value, search: search.value.trim(),
      };
      load();
    }

    mount(view,
      el('div', { class: 'screen-head' },
        el('h1', { class: 'screen-title' }, T.paymentsTitle),
      ),

      el('form', { class: 'filter-bar', onSubmit: apply },
        el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.from), from),
        el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.to), to),
        el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.colFor), kind),
        el('div', { class: 'field' }, el('label', { class: 'field-label' }, T.colMethod), method),
        el('div', { class: 'field filter-search' },
          el('label', { class: 'field-label' }, T.search), search),
        el('button', { class: 'btn btn-primary', type: 'submit' }, T.search),
      ),

      loading ? spinner() : summary(),
      loading
        ? null
        : rows.length === 0
          ? el('p', { class: 'empty' }, T.noPaymentsMatch)
          : table(),
    );
  }

  // The three numbers anyone reconciling a day actually reads out.
  function summary() {
    const collected = rows.reduce((n, p) => n + Number(p.amount || 0), 0);
    const discounted = rows.reduce((n, p) => n + Number(p.discount_amount || 0), 0);
    return el('p', { class: 'summary-line' },
      T.paymentsTotal(rows.length, money(collected), money(discounted)));
  }

  function table() {
    return el('div', { class: 'table-wrap' },
      el('table', { class: 'table' },
        el('thead', {},
          el('tr', {},
            el('th', {}, T.colDate),
            el('th', {}, T.colMember),
            el('th', {}, T.colFor),
            el('th', { class: 'num' }, T.colFee),
            el('th', { class: 'num' }, T.colDiscount),
            el('th', { class: 'num' }, T.colCollected),
            el('th', {}, T.colMethod),
            el('th', {}, T.colReference),
            el('th', {}, T.colTakenBy),
          )),
        el('tbody', {}, ...rows.map(row)),
      ));
  }

  function row(p) {
    return el('tr', {},
      el('td', { class: 'cell-muted cell-nowrap' }, formatDate(p.paid_at)),
      el('td', { class: 'cell-nowrap' },
        el('a', { href: `#/members/${p.member_id}`, class: 'cell-strong' }, p.member_name),
        el('span', { class: 'cell-sub' }, p.member_code || ''),
      ),
      el('td', {}, el('span', { class: 'pill' }, T.kindShort[p.kind] ?? p.kind)),
      // Money right-aligned so a column of figures can be scanned down, and
      // zero discounts shown as a dash rather than a 0 competing for attention.
      el('td', { class: 'num cell-muted' }, money(p.gross_amount ?? p.amount)),
      el('td', { class: 'num' },
        Number(p.discount_amount) > 0
          ? el('span', { class: 'pill pill-warn' }, money(p.discount_amount))
          : el('span', { class: 'cell-muted' }, '—')),
      el('td', { class: 'num cell-strong' }, money(p.amount)),
      el('td', { class: 'cell-muted' },
        [T.methods[p.method] ?? p.method, p.bank_account_name].filter(Boolean).join(' · ')),
      el('td', { class: 'cell-copy-code' }, p.receipt_no || '—'),
      el('td', { class: 'cell-muted' }, p.recorded_by_name || '—'),
    );
  }

  load();
  return { destroy: () => {} };
}
